/**
 * DesignPartnerAgreement — /design-partner/:token
 *
 * Public (no auth), click-to-sign / click-to-pay Design Partner Pilot
 * Agreement page. Replaces the DocuSign-based flow (Matt, 2026-08).
 *
 * Flow: fetch scope by token → show scope + collapsible T&Cs → sign
 * (name/title/checkbox) → if priced, redirect to Stripe Checkout; if free,
 * show a signed confirmation. After returning from Stripe success_url,
 * re-fetch by token to confirm paid status.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router'
import Nav from '../components/Nav'
import Footer from '../components/Footer'
import { useThemeColors } from '../hooks/useThemeColors'
import {
  getDesignPartnerAgreement,
  signDesignPartnerAgreement,
  createDesignPartnerCheckoutSession,
  type DesignPartnerAgreementPublic,
} from '../lib/api'
import { trackDesignPartnerSigned, trackDesignPartnerCheckoutStarted } from '../lib/analytics'

type PageState = 'loading' | 'invalid' | 'ready' | 'signing' | 'signed_free' | 'redirecting' | 'paid'

const GENERIC_TERMS_INTRO = `This Design Partner Pilot Agreement is a generic SaaS-pilot-agreement style
template covering data handling, IP ownership, term length, warranty
disclaimer, confidentiality, and termination.`

export default function DesignPartnerAgreement() {
  const c = useThemeColors()
  const { token } = useParams<{ token?: string }>()
  const [searchParams] = useSearchParams()
  const paymentReturn = searchParams.get('payment')

  const [state, setState] = useState<PageState>('loading')
  const [agreement, setAgreement] = useState<DesignPartnerAgreementPublic | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [termsOpen, setTermsOpen] = useState(false)

  const [signerName, setSignerName] = useState('')
  const [signerTitle, setSignerTitle] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [signError, setSignError] = useState('')

  const fetchAgreement = useCallback(async () => {
    if (!token) {
      setState('invalid')
      return
    }
    try {
      const data = await getDesignPartnerAgreement(token)
      setAgreement(data)
      if (data.status === 'paid') {
        setState('paid')
      } else if (data.status === 'signed' && (!data.priceUsd || data.priceUsd <= 0)) {
        setState('signed_free')
      } else {
        setState('ready')
      }
    } catch (err) {
      const e = err as { status?: number; message?: string }
      setErrorMsg(e.message ?? 'This link is no longer valid.')
      setState('invalid')
    }
  }, [token])

  useEffect(() => {
    fetchAgreement()
  }, [fetchAgreement])

  // After returning from Stripe success_url, re-check status.
  useEffect(() => {
    if (paymentReturn === 'success' && token) {
      fetchAgreement()
    }
  }, [paymentReturn, token, fetchAgreement])

  async function handleSign() {
    if (!token || !signerName.trim() || !signerTitle.trim() || !agreed) return
    setState('signing')
    setSignError('')
    try {
      const result = await signDesignPartnerAgreement(token, {
        signerName: signerName.trim(),
        signerTitle: signerTitle.trim(),
        agreedToTerms: true,
      })
      if (result.requiresPayment) {
        setState('redirecting')
        trackDesignPartnerCheckoutStarted()
        const { checkoutUrl } = await createDesignPartnerCheckoutSession(token)
        if (checkoutUrl) {
          window.location.href = checkoutUrl
        } else {
          setSignError('Could not start payment. Please refresh and try again.')
          setState('ready')
        }
      } else {
        trackDesignPartnerSigned()
        setState('signed_free')
      }
    } catch (err) {
      const e = err as { status?: number; message?: string }
      if (e.status === 409) {
        // Already signed by the time this request landed — re-fetch truth.
        await fetchAgreement()
      } else {
        setSignError(e.message ?? 'Something went wrong. Please try again.')
        setState('ready')
      }
    }
  }

  // ── Styles ──────────────────────────────────────────────────────────────
  const container: React.CSSProperties = { maxWidth: 700, margin: '0 auto', padding: '96px 24px' }
  const label: React.CSSProperties = {
    fontFamily: '"JetBrains Mono", monospace', fontSize: 11, color: c.signal,
    textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 16px',
  }
  const h1: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 40, letterSpacing: '-0.03em',
    color: c.textPrimary, margin: '0 0 12px', lineHeight: 1.1,
  }
  const body: React.CSSProperties = { color: c.textMuted, fontSize: 16, lineHeight: '1.65', margin: '0 0 12px' }
  const scopeBox: React.CSSProperties = {
    background: c.bgSurface, border: `1px solid ${c.border}`, borderRadius: 10,
    padding: '24px 28px', margin: '24px 0 32px',
  }
  const scopeRow: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: `1px solid ${c.border}` }
  const scopeKey: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }
  const scopeVal: React.CSSProperties = { fontSize: 15, color: c.textPrimary, textAlign: 'right' }
  const priceHighlight: React.CSSProperties = { fontSize: 22, fontWeight: 800, color: c.signal }
  const input: React.CSSProperties = {
    width: '100%', padding: '10px 14px', borderRadius: 6, border: `1px solid ${c.border}`,
    background: c.bgPrimary, color: c.textPrimary, fontSize: 15, fontFamily: 'Inter, sans-serif',
    marginBottom: 16, boxSizing: 'border-box',
  }
  const btnPrimary = (disabled: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '12px 24px', borderRadius: 6, fontFamily: 'Inter, sans-serif', fontSize: 15,
    fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
    background: c.signal, color: '#fff', border: 'none',
  })

  // ── Loading ──────────────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
        <Nav scrolled={false} />
        <div style={container}>
          <p style={body}>Loading…</p>
        </div>
        <Footer />
      </div>
    )
  }

  // ── Invalid / expired ────────────────────────────────────────────────────
  if (state === 'invalid') {
    return (
      <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
        <Nav scrolled={false} />
        <div style={container}>
          <p style={label}>DESIGN PARTNER AGREEMENT</p>
          <h1 style={h1}>This link is no longer valid</h1>
          <p style={body}>{errorMsg || 'The agreement link you followed has expired or does not exist. Please reach out to your Primate Intelligence contact for a new link.'}</p>
        </div>
        <Footer />
      </div>
    )
  }

  if (!agreement) return null

  const priceLabel = agreement.priceUsd && agreement.priceUsd > 0
    ? `$${agreement.priceUsd.toLocaleString()}`
    : 'No cost — pilot'

  // ── Paid confirmation ────────────────────────────────────────────────────
  if (state === 'paid') {
    return (
      <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
        <Nav scrolled={false} />
        <div style={container}>
          <p style={label}>DESIGN PARTNER AGREEMENT</p>
          <h1 style={h1}>All set — signed and paid</h1>
          <p style={body}>
            Thanks — the pilot agreement for <strong style={{ color: c.textPrimary }}>{agreement.companyLegalName}</strong> is
            signed and payment has been received. A copy of the signed agreement has been emailed to Primate Intelligence.
          </p>
        </div>
        <Footer />
      </div>
    )
  }

  // ── Signed, free pilot confirmation ──────────────────────────────────────
  if (state === 'signed_free') {
    return (
      <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
        <Nav scrolled={false} />
        <div style={container}>
          <p style={label}>DESIGN PARTNER AGREEMENT</p>
          <h1 style={h1}>Signed!</h1>
          <p style={body}>
            Thanks — the pilot agreement for <strong style={{ color: c.textPrimary }}>{agreement.companyLegalName}</strong> has
            been signed. A copy has been emailed to Primate Intelligence.
          </p>
        </div>
        <Footer />
      </div>
    )
  }

  // ── Ready / signing / redirecting ────────────────────────────────────────
  const canSign = !!signerName.trim() && !!signerTitle.trim() && agreed
  const busy = state === 'signing' || state === 'redirecting'

  return (
    <div style={{ background: c.bgPrimary, minHeight: '100vh' }}>
      <Nav scrolled={false} />
      <div style={container}>
        <p style={label}>DESIGN PARTNER AGREEMENT</p>
        <h1 style={h1}>Design Partner Pilot Agreement</h1>
        <p style={body}>
          Review the scope of this pilot below, then sign to get started.
        </p>

        {/* Scope of this Pilot */}
        <div style={scopeBox} data-testid="scope-box">
          <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
            Scope of this Pilot
          </div>
          <div style={scopeRow}>
            <span style={scopeKey}>Company</span>
            <span style={scopeVal}>{agreement.companyLegalName}</span>
          </div>
          <div style={scopeRow}>
            <span style={scopeKey}>Use Case</span>
            <span style={scopeVal}>{agreement.useCase}</span>
          </div>
          {agreement.valueOfSolving && (
            <div style={scopeRow}>
              <span style={scopeKey}>Value of Solving</span>
              <span style={scopeVal}>{agreement.valueOfSolving}</span>
            </div>
          )}
          <div style={scopeRow}>
            <span style={scopeKey}>Target Metrics</span>
            <span style={scopeVal}>{agreement.targetMetrics}</span>
          </div>
          {agreement.scopeOfWork && (
            <div style={scopeRow}>
              <span style={scopeKey}>Scope of Work</span>
              <span style={scopeVal}>{agreement.scopeOfWork}</span>
            </div>
          )}
          {agreement.estimatedCostToDeliver && (
            <div style={scopeRow}>
              <span style={scopeKey}>Estimated Cost to Deliver</span>
              <span style={scopeVal}>{agreement.estimatedCostToDeliver}</span>
            </div>
          )}
          <div style={scopeRow}>
            <span style={scopeKey}>Price</span>
            <span style={priceHighlight}>{priceLabel}</span>
          </div>
          <div style={{ ...scopeRow, borderBottom: 'none' }}>
            <span style={scopeKey}>Public Reference</span>
            <span style={scopeVal}>
              {agreement.publicReferenceOptOut
                ? 'Opted out — private (investor-only) reference retained'
                : 'Approved — public quote/logo use permitted'}
            </span>
          </div>
        </div>

        {/* Collapsed Terms & Conditions */}
        <div style={{ marginBottom: 32 }}>
          <button
            type="button"
            onClick={() => setTermsOpen(o => !o)}
            data-testid="terms-toggle"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontFamily: 'Inter, sans-serif', fontSize: 15, fontWeight: 600, color: c.signal,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            {termsOpen ? '▾' : '▸'} Full Terms &amp; Conditions
          </button>
          {termsOpen && (
            <div style={{ marginTop: 16, fontSize: 14, color: c.textMuted, lineHeight: 1.7 }}>
              <p>{GENERIC_TERMS_INTRO}</p>
              <p><strong style={{ color: c.textPrimary }}>Term:</strong> 90 days from signature, unless terminated earlier.</p>
              <p><strong style={{ color: c.textPrimary }}>Data handling:</strong> Primate processes Partner data solely to provide and improve the pilot, and does not sell it to third parties.</p>
              <p><strong style={{ color: c.textPrimary }}>IP:</strong> Primate retains ownership of its models and technology; Partner retains ownership of its own business data. Feedback provided during the pilot may be used to improve Primate's products.</p>
              <p><strong style={{ color: c.textPrimary }}>Warranty:</strong> The pilot is a beta offering provided "as is," with no warranty of uptime or fitness for production use.</p>
              <p><strong style={{ color: c.textPrimary }}>Confidentiality:</strong> Mutual — both parties keep non-public information confidential for 2 years post-termination.</p>
              <p><strong style={{ color: c.textPrimary }}>Termination:</strong> Either party may terminate for convenience with written notice (email is sufficient).</p>
              <p><strong style={{ color: c.textPrimary }}>Default scope:</strong> Unless a different scope is specified above, Primate's standard scope includes free (not discounted) API access during the pilot term, a direct feedback line to Primate's founders, and a co-authored case study.</p>
              <p><strong style={{ color: c.textPrimary }}>Public/private reference:</strong> {agreement.publicReferenceOptOut
                ? "Partner has opted OUT of public use of its name, logo, and/or quotes. Primate will not use Partner's name, logo, or quotes publicly under this Agreement."
                : "Partner has agreed to public use of its name, logo, and/or a pre-approved quote in Primate's website, case study, and launch materials."} Regardless of this choice, Primate may always reference Partner privately in materials shared with Primate's investors and prospective investors — this private reference right is non-negotiable and independent of the public-reference choice.</p>
            </div>
          )}
        </div>

        {/* Signature block */}
        <div style={{ borderTop: `1px solid ${c.border}`, paddingTop: 28 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>
            Sign this Agreement
          </div>
          <label style={{ fontSize: 13, color: c.textMuted, display: 'block', marginBottom: 6 }}>Full Name</label>
          <input
            style={input}
            value={signerName}
            onChange={e => setSignerName(e.target.value)}
            placeholder="Jane Doe"
            data-testid="signer-name-input"
            disabled={busy}
          />
          <label style={{ fontSize: 13, color: c.textMuted, display: 'block', marginBottom: 6 }}>Title</label>
          <input
            style={input}
            value={signerTitle}
            onChange={e => setSignerTitle(e.target.value)}
            placeholder="CTO"
            data-testid="signer-title-input"
            disabled={busy}
          />
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: c.textMuted, marginBottom: 24, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              data-testid="agree-checkbox"
              disabled={busy}
              style={{ marginTop: 2 }}
            />
            I have the authority to bind {agreement.companyLegalName} to this agreement
          </label>

          {signError && (
            <p style={{ ...body, color: '#DC2626', marginBottom: 16 }}>{signError}</p>
          )}

          <button
            type="button"
            onClick={handleSign}
            disabled={!canSign || busy}
            data-testid="sign-btn"
            style={btnPrimary(!canSign || busy)}
          >
            {state === 'redirecting' ? 'Redirecting to payment…' : state === 'signing' ? 'Signing…' : 'Sign Agreement'}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  )
}
